/**
 * Milestones are the visible spine of progression: the goals list on the phone
 * and the evidence for the PROGRESSION quality gate.
 */
import type { GameState } from '../state/GameState.js';
import type { EventBus } from '../core/events.js';
import type { FinanceSystem } from './FinanceSystem.js';
import { BALANCE } from '../data/balance.js';

interface MilestoneDef {
  id: string;
  label: string;
  netWorth?: number | null;
  cashEarned?: number;
  equipmentCount?: number;
  businessRegistered?: boolean;
  employeeCount?: number;
}

export class MilestoneSystem {
  constructor(private readonly bus: EventBus) {}

  check(state: GameState, finance: FinanceSystem): void {
    const netWorth = finance.netWorth(state);
    if (netWorth > state.statistics.peakNetWorth) {
      state.statistics.peakNetWorth = netWorth;
    }

    for (const raw of BALANCE.milestones as readonly MilestoneDef[]) {
      if (state.milestones.includes(raw.id)) continue;
      if (!this.satisfied(raw, state, netWorth)) continue;
      state.milestones.push(raw.id);
      this.bus.emit({ type: 'milestone.reached', id: raw.id, label: raw.label });
    }
  }

  private satisfied(def: MilestoneDef, state: GameState, netWorth: number): boolean {
    if (def.netWorth != null && netWorth < def.netWorth) return false;
    if (def.cashEarned != null && state.finance.lifetimeEarned < def.cashEarned) return false;
    if (def.equipmentCount != null && state.business.equipment.length < def.equipmentCount) {
      return false;
    }
    if (def.businessRegistered && !state.business.registered) return false;
    if (def.employeeCount != null && state.business.employees.length < def.employeeCount) {
      return false;
    }
    return true;
  }

  /** Next unreached milestone, for the phone's goal display. */
  next(state: GameState): { id: string; label: string } | null {
    for (const m of BALANCE.milestones as readonly MilestoneDef[]) {
      if (!state.milestones.includes(m.id)) return { id: m.id, label: m.label };
    }
    return null;
  }
}
